import { useQuery, useReactiveVar } from '@apollo/client/react';
import { useContext, useEffect, useState } from 'react';

import { MANAGER_SUB_AREAS } from '../constants';
import { type QAFormData, type SeverityLevel } from '../types';

import CurrentUserContext from '@/Contexts/CurrentUser';
import { GetCurrentUserDocument } from '@/graphql/generated/graphql';
import { activeTabVar } from '@/graphql/reactiveVars';
import { rootRouteModeVar } from '@/router/reactiveRoutes';

export interface UseQAFormResult {
  formData: QAFormData;
  setTitle: (val: string) => void;
  setDescription: (val: string) => void;
  setSeverity: (val: SeverityLevel) => void;
  setArea: (val: string) => void;
  setSubArea: (val: string) => void;
  setStepsToReproduce: (val: string) => void;
  setExpectedResult: (val: string) => void;
  setActualResult: (val: string) => void;
  setReporterName: (val: string) => void;
  setReporterEmail: (val: string) => void;
  resetForm: () => void;
}

export const useQAForm = (isOpen: boolean): UseQAFormResult => {
  const mode = useReactiveVar(rootRouteModeVar);
  const activeTab = useReactiveVar(activeTabVar);
  const { currentUser: contextUser } = useContext(CurrentUserContext);
  const { data: userData } = useQuery(GetCurrentUserDocument, {
    fetchPolicy: 'cache-first',
  });
  const currentUser = contextUser || userData?.fetchMyData;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<SeverityLevel>('Medium');
  const [area, setArea] = useState<string>('Manager');
  const [subArea, setSubArea] = useState<string>('Dashboard');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [expectedResult, setExpectedResult] = useState('');
  const [actualResult, setActualResult] = useState('');
  const [reporterName, setReporterName] = useState('');
  const [reporterEmail, setReporterEmail] = useState('');

  // Pre-fill user details from Apollo cache / context
  useEffect(() => {
    if (currentUser) {
      const fullName = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ');
      if (fullName) setReporterName((prev) => prev || fullName);
      if (currentUser.email) setReporterEmail((prev) => prev || currentUser.email);
    }
  }, [currentUser, isOpen]);

  // Auto-detect Area and Sub-category based on mode and active tab
  useEffect(() => {
    const path = typeof window !== 'undefined' ? window.location.pathname.toLowerCase() : '';

    if (path.includes('onboarding')) {
      setArea('Onboarding');
    } else if (path.includes('login') || path.includes('register') || path.includes('auth')) {
      setArea('Auth / Account');
    } else if (mode === 'scheduler') {
      setArea('Scheduler');
    } else if (mode === 'report') {
      setArea('Reports');
    } else if (mode === 'funnel') {
      setArea('Funnel');
    } else if (mode === 'manager' || mode === 'super') {
      setArea('Manager');
      if (activeTab && MANAGER_SUB_AREAS.includes(activeTab as any)) {
        setSubArea(activeTab);
      }
    } else {
      setArea('Other');
    }
  }, [mode, activeTab, isOpen]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setStepsToReproduce('');
    setExpectedResult('');
    setActualResult('');
    if (currentUser) {
      const fullName = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ');
      if (fullName) setReporterName(fullName);
      if (currentUser.email) setReporterEmail(currentUser.email);
    }
  };

  return {
    formData: {
      title,
      description,
      severity,
      area,
      subArea,
      stepsToReproduce,
      expectedResult,
      actualResult,
      reporterName,
      reporterEmail,
    },
    setTitle,
    setDescription,
    setSeverity,
    setArea,
    setSubArea,
    setStepsToReproduce,
    setExpectedResult,
    setActualResult,
    setReporterName,
    setReporterEmail,
    resetForm,
  };
};
